/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { useCallback, useMemo, useState, type JSX } from 'react';
import { useTranslation } from 'react-i18next';

import {
  type TimesheetPeriod,
  TimesheetView,
  rangeFor,
} from '../../components/timesheet/timesheet-view.js';
import { useTherapistTimesheet } from '../../lib/queries/timesheet.js';

/**
 * Admin → Therapist Profile → Timesheet (BRD v2.0 CR-004). The therapist's
 * auto-filled, session-based timesheet (amounts apply the per-status pay rules,
 * incl. the consultant 30% absent rule) — the basis for the consultant invoice.
 */
export function AdminTherapistTimesheetPanel({
  therapistId,
}: {
  readonly therapistId: string;
}): JSX.Element {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<TimesheetPeriod>('this_month');
  const range = useMemo(() => rangeFor(period), [period]);
  const query = useTherapistTimesheet(therapistId, range);
  const onPeriod = useCallback((p: TimesheetPeriod) => {
    setPeriod(p);
  }, []);

  return (
    <div className="space-y-3">
      <p className="text-sm font-semibold text-slate-800 dark:text-white">{t('timesheet.title')}</p>
      <TimesheetView
        data={query.data}
        isLoading={query.isLoading}
        isError={query.isError}
        period={period}
        onPeriod={onPeriod}
      />
    </div>
  );
}
